import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Paper,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    LinearProgress,
    Chip,
    Tooltip,
    Alert,
    IconButton,
    Autocomplete,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    FolderOpen as FolderOpenIcon,
    CloudUpload as CloudUploadIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    HelpOutline as HelpIcon,
    PhotoCamera as PhotoCameraIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Check as ConfirmIcon,
    Close as CancelIcon,
    ClearAll as ClearAllIcon,
    CloudOff as CloudOffIcon,
} from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { getCategoryNames, getProductNames, uploadProductImages, deleteProductImage } from '../../../shared/services/productService';
import { resolveImageUrl } from '../../../shared/utils/imageUrl';
import { ProductImage } from '../../../shared/components/ProductImage';

type ProductName = { productId: number; name: string; imageUrl?: string | string[] };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type RowStatus = 'pending' | 'uploading' | 'success' | 'error' | 'no-match' | 'duplicate' | 'update';

interface ImageRow {
    id: number;
    file: File;
    previewUrl: string;
    displayName: string;
    matchedProduct: ProductName | null;
    status: RowStatus;
    errorMessage?: string;
}

interface StoredEntry {
    id: number;
    file: File;
    displayName: string;
}

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = 'bulk-image-upload-db';
const STORE = 'files';

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function saveEntriesToDB(entries: StoredEntry[]): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        store.clear();
        entries.forEach((e) => store.put(e));
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

async function loadEntriesFromDB(): Promise<StoredEntry[]> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).getAll();
        req.onsuccess = () => resolve(req.result as StoredEntry[]);
        req.onerror = () => reject(req.error);
    });
}

async function clearDB(): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).clear();
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function normalize(str: string): string {
    return str
        .toLowerCase()
        .replace(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i, '')
        .replace(/[-_]/g, ' ')
        .trim();
}

function findMatch(displayName: string, products: ProductName[]): ProductName | null {
    const fileKey = normalize(displayName);
    if (!fileKey) return null;

    // 1. Try exact match first (after normalization)
    const exact = products.find((p) => normalize(p.name) === fileKey);
    if (exact) return exact;

    // 2. No more partial/substring matches allowed to prevent false matches.
    // If it's not an exact match, it will be "No Match".
    return null;
}

let rowCounter = 0;

function buildRowsFromEntries(entries: StoredEntry[], products: ProductName[]): ImageRow[] {
    const rows: ImageRow[] = entries.map((entry) => {
        const matched = findMatch(entry.displayName, products);
        return {
            id: entry.id,
            file: entry.file,
            previewUrl: URL.createObjectURL(entry.file),
            displayName: entry.displayName,
            matchedProduct: matched,
            status: matched ? 'pending' : 'no-match',
        };
    });
    return refreshRowStatuses(rows);
}

function refreshRowStatuses(rows: ImageRow[]): ImageRow[] {
    const matchedProductIds = new Set<number>();
    return rows.map((row) => {
        // Keep success/uploading as is, but mark their product as matched
        if (row.status === 'success' || row.status === 'uploading') {
            if (row.matchedProduct) matchedProductIds.add(row.matchedProduct.productId);
            return row;
        }

        if (!row.matchedProduct) {
            return { ...row, status: 'no-match', errorMessage: undefined };
        }

        if (matchedProductIds.has(row.matchedProduct.productId)) {
            return {
                ...row,
                status: 'duplicate',
                errorMessage: `Multiple images matched to product: ${row.matchedProduct.name}`,
            };
        }

        matchedProductIds.add(row.matchedProduct.productId);
        
        // Check if product already has an image on the server
        const status: RowStatus = resolveImageUrl(row.matchedProduct.imageUrl) ? 'update' : 'pending';
        
        return { ...row, status, errorMessage: undefined };
    });
}

// ---------------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------------

const StatusChip: React.FC<{ status: RowStatus; errorMessage?: string }> = ({ status, errorMessage }) => {
    switch (status) {
        case 'pending':
            return <Chip label="Ready" size="small" color="default" variant="outlined" />;
        case 'update':
            return <Tooltip title="This product already has an image. Uploading will REPLACE it."><Chip label="Already Exists" size="small" color="primary" variant="outlined" /></Tooltip>;
        case 'uploading':
            return <Chip label="Uploading..." size="small" color="info" icon={<CircularProgress size={12} />} />;
        case 'success':
            return <Chip label="Uploaded" size="small" color="success" icon={<CheckCircleIcon />} />;
        case 'error':
            return (
                <Tooltip title={errorMessage ?? 'Upload failed'}>
                    <Chip label="Failed" size="small" color="error" icon={<ErrorIcon />} />
                </Tooltip>
            );
        case 'no-match':
            return (
                <Tooltip title="No product matched — rename to fix">
                    <Chip label="No Match" size="small" color="warning" icon={<HelpIcon />} />
                </Tooltip>
            );
        case 'duplicate':
            return (
                <Tooltip title={errorMessage ?? 'Duplicate match'}>
                    <Chip label="Duplicate" size="small" color="error" variant="outlined" icon={<ErrorIcon />} />
                </Tooltip>
            );
    }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BulkImageUpload: React.FC = () => {
    const navigate = useNavigate();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const folderInputRef = useRef<HTMLInputElement>(null);

    const [products, setProducts] = useState<ProductName[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [rows, setRows] = useState<ImageRow[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [globalError, setGlobalError] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [categories, setCategories] = useState<string[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [deleteConfirmParams, setDeleteConfirmParams] = useState<{ productId: number; productName: string } | null>(null);
    const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
    const [uploadConfirmOpen, setUploadConfirmOpen] = useState(false);
    const isPageRefreshRef = useRef(false);

    // Load initial data
    useEffect(() => {
        (async () => {
            try {
                setLoadingCategories(true);
                const [prodRes, catRes] = await Promise.all([
                    getProductNames(selectedCategory || undefined),
                    getCategoryNames()
                ]);
                
                const items = prodRes?.data ?? [];
                setProducts(items);
                setCategories(catRes?.data ?? []);

                // Restore from IndexedDB
                const stored = await loadEntriesFromDB();
                if (stored.length > 0) {
                    rowCounter = Math.max(...stored.map((e) => e.id), rowCounter);
                    setRows(buildRowsFromEntries(stored, items));
                }
            } catch {
                setGlobalError('Failed to load data. Please go back and try again.');
            } finally {
                setLoadingProducts(false);
                setLoadingCategories(false);
            }
        })();
    }, []);

    // Handle category change - re-fetch products
    const handleCategoryChange = async (category: string | null) => {
        setSelectedCategory(category);
        setLoadingProducts(true);
        try {
            const res = await getProductNames(category || undefined);
            const items = res?.data ?? [];
            setProducts(items);
            
            // Re-match and refresh statuses
            setRows((prevRows) => {
                const updatedRows = prevRows.map((row) => {
                    if (row.status === 'success' || row.status === 'uploading') return row;
                    const matched = findMatch(row.displayName, items);
                    return {
                        ...row,
                        matchedProduct: matched,
                    };
                });
                return refreshRowStatuses(updatedRows);
            });
        } catch {
            setGlobalError('Failed to refresh products for this category.');
        } finally {
            setLoadingProducts(false);
        }
    };

    // Detect page refresh (Ctrl+R / F5) vs SPA navigation
    useEffect(() => {
        const onBeforeUnload = () => { isPageRefreshRef.current = true; };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, []);

    // On unmount: clear DB only if navigating away (not on refresh)
    useEffect(() => {
        return () => {
            if (!isPageRefreshRef.current) {
                clearDB().catch(() => {});
            }
        };
    }, []);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilesSelected = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files ?? []).filter((f) =>
                f.type.startsWith('image/')
            );
            if (!files.length) return;

            const newEntries: StoredEntry[] = files.map((file) => ({
                id: ++rowCounter,
                file,
                displayName: file.name,
            }));

            // old selected files + new files merge
            const existingEntries: StoredEntry[] = rows.map((r) => ({
                id : r.id,
                file: r.file,
                displayName: r.displayName
            }));

            const existingNames = new Set(existingEntries.map((e) => e.displayName.toLowerCase()));
            const filteredNewEntries = newEntries.filter((e) => !existingNames.has(e.displayName.toLowerCase()));

            if (filteredNewEntries.length < newEntries.length) {
                setGlobalError('Some files were skipped because they have the same name as existing entries.');
            }

            const mergedEntries = [...existingEntries, ...filteredNewEntries];

            // save merge data
            await saveEntriesToDB(mergedEntries);
            // Build rows again
            setRows(buildRowsFromEntries(mergedEntries, products));

            // reset input
            setUploadProgress(0);
            setEditingId(null);

            if (fileInputRef.current) fileInputRef.current.value = '';
            if (folderInputRef.current) folderInputRef.current.value = '';
        },
        [products, rows]
    );

    // ---- Rename ----
    const startEdit = (row: ImageRow) => {
        setEditingId(row.id);
        setEditValue(row.displayName);
    };

    const commitEdit = useCallback(
        async (id: number) => {
            const trimmed = editValue.trim();
            if (!trimmed) return;
            const updated = rows.map((r) => {
                if (r.id !== id) return r;
                const matched = findMatch(trimmed, products);
                return {
                    ...r,
                    displayName: trimmed,
                    matchedProduct: matched,
                    errorMessage: undefined,
                };
            });
            setRows(refreshRowStatuses(updated));
            setEditingId(null);

            // Persist updated displayNames
            const entries: StoredEntry[] = updated.map((r) => ({ id: r.id, file: r.file, displayName: r.displayName }));
            await saveEntriesToDB(entries);
        },
        [editValue, rows, products]
    );

    const cancelEdit = () => setEditingId(null);

    // ---- Remove ----
    const removeRow = useCallback(
        async (id: number) => {
            const updated = rows.filter((r) => {
                if (r.id === id) { URL.revokeObjectURL(r.previewUrl); return false; }
                return true;
            });
            const refreshed = refreshRowStatuses(updated);
            setRows(refreshed);
            if (editingId === id) setEditingId(null);
            const entries: StoredEntry[] = refreshed.map((r) => ({ id: r.id, file: r.file, displayName: r.displayName }));
            await saveEntriesToDB(entries);
        },
        [rows, editingId]
    );

    // ---- Clear all ----
    const handleClear = useCallback(() => {
        setClearConfirmOpen(true);
    }, []);

    const executeClear = useCallback(async () => {
        rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
        setRows([]);
        setEditingId(null);
        setUploadProgress(0);
        await clearDB();
        setClearConfirmOpen(false);
    }, [rows]);

    // ---- Delete Existing Image from Server ----
    const executeRemoveServerImage = async () => {
        if (!deleteConfirmParams) return;
        const { productId } = deleteConfirmParams;
        setDeleteConfirmParams(null);
        
        try {
            await deleteProductImage(productId);
            // Update local state to reflect that the server no longer has the image
            setRows((prevRows) => {
                const updatedRows = prevRows.map((r) => {
                    if (r.matchedProduct?.productId === productId) {
                        return {
                            ...r,
                            matchedProduct: { ...r.matchedProduct!, imageUrl: undefined }
                        };
                    }
                    return r;
                });
                return refreshRowStatuses(updatedRows);
            });
        } catch (err) {
            console.error(err);
            setGlobalError('Failed to remove image from server');
        }
    };

    // ---- Upload all ----
    const uploadAll = async () => {
        const pending = rows.filter((r) => r.status === 'pending' || r.status === 'update');
        if (!pending.length) return;

        setUploading(true);
        setUploadProgress(0);
        let done = 0;

        for (const row of pending) {
            const product = row.matchedProduct!;
            const id = String(product.productId);

            const renamedFile = new File(
            [row.file],
            row.displayName,
            { type: row.file.type });

            setRows((prev) =>
                prev.map((r) => (r.id === row.id ? { ...r, status: 'uploading' } : r))
            );

            try {
                const res = await uploadProductImages(id, [renamedFile]);
                setRows((prev) =>
                    prev.map((r) =>
                        r.id === row.id
                            ? {
                                  ...r,
                                  status: res.success ? 'success' : 'error',
                                  errorMessage: res.success ? undefined : (res.message ?? 'Upload failed'),
                                  matchedProduct:
                                      res.success && res.data?.imageUrl
                                          ? { ...r.matchedProduct!, imageUrl: res.data.imageUrl }
                                          : r.matchedProduct,
                              }
                            : r
                    )
                );
            } catch {
                setRows((prev) =>
                    prev.map((r) =>
                        r.id === row.id ? { ...r, status: 'error', errorMessage: 'Upload failed' } : r
                    )
                );
            }

            done += 1;
            setUploadProgress(Math.round((done / pending.length) * 100));
        }

        setUploading(false);
    };

    const pendingCount = rows.filter((r) => r.status === 'pending' || r.status === 'update').length;
    const readyToUploadCount = rows.filter((r) => r.status === 'pending').length;
    const updateCount = rows.filter((r) => r.status === 'update').length;
    const successCount = rows.filter((r) => r.status === 'success').length;
    const errorCount = rows.filter((r) => r.status === 'error').length;
    const noMatchCount = rows.filter((r) => r.status === 'no-match').length;
    const duplicateCount = rows.filter((r) => r.status === 'duplicate').length;

    return (
        <div className="flex flex-col flex-auto min-w-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex items-center gap-4">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/product')}>
                        Back to Products
                    </AppButton>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Bulk Image Upload</h2>
                </div>
            </div>

            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col gap-6 w-full">
                    {/* Instructions */}
                    <Paper className="p-4 sm:p-6 !shadow-sm !border !border-gray-200">
                        <h3 className="text-base font-semibold text-gray-800 mb-2">How it works</h3>
                        <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                            <li>Click <strong>"Select Images"</strong> or <strong>"Select Folder"</strong> to choose images.</li>
                            <li>Each filename is automatically matched to a product by name.</li>
                            <li>"No Match" rows — click the <strong>edit (pencil) icon</strong> to rename and re-match.</li>
                            <li>Click the <strong>delete icon</strong> to remove any unwanted row.</li>
                            <li>Click <strong>"Upload All"</strong> to upload all matched images.</li>
                        </ol>
                        <p className="text-xs text-gray-400 mt-2">
                            Tip: <code>Tata Salt 1kg.jpg</code> → matches product <em>"Tata Salt 1kg"</em> (case-insensitive, dashes/underscores = spaces).
                            Your selection is saved — page refresh will restore the list.
                        </p>
                    </Paper>

                    {globalError && <Alert severity="error">{globalError}</Alert>}

                    {/* Action bar */}
                    <div className="flex items-center gap-4 flex-wrap">
                        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFilesSelected} />
                        <input
                            ref={folderInputRef}
                            type="file"
                            accept="image/*"
                            // @ts-expect-error webkitdirectory is non-standard but widely supported
                            webkitdirectory=""
                            className="hidden"
                            onChange={handleFilesSelected}
                        />

                        <Autocomplete
                            options={categories}
                            value={selectedCategory}
                            onChange={(_, val) => handleCategoryChange(val)}
                            loading={loadingCategories}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Category"
                                    size="small"
                                    placeholder="Select a category..."
                                    sx={{ minWidth: 250 }}
                                />
                            )}
                            sx={{ mr: 2 }}
                        />

                        <AppButton
                            variant="secondary"
                            startIcon={<PhotoCameraIcon />}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={loadingProducts || uploading}
                            loading={loadingProducts}
                        >
                            {loadingProducts ? 'Loading products...' : 'Select Images'}
                        </AppButton>
                        <AppButton
                            variant="secondary"
                            startIcon={<FolderOpenIcon />}
                            onClick={() => folderInputRef.current?.click()}
                            disabled={loadingProducts || uploading}
                        >
                            Select Folder
                        </AppButton>

                        {rows.length > 0 && (
                            <AppButton
                                variant="primary"
                                startIcon={<CloudUploadIcon />}
                                onClick={() => setUploadConfirmOpen(true)}
                                disabled={uploading || pendingCount === 0}
                                loading={uploading}
                            >
                                {uploading ? 'Uploading...' : `Upload All (${pendingCount} images)`}
                            </AppButton>
                        )}

                        {rows.length > 0 && !uploading && (
                            <AppButton
                                variant="secondary"
                                startIcon={<ClearAllIcon />}
                                onClick={handleClear}
                            >
                                Clear
                            </AppButton>
                        )}

                        {rows.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap ml-auto">
                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{rows.length} files</span>
                                {successCount > 0 && <span className="text-xs font-medium text-green-700 bg-green-100 px-3 py-1 rounded-full">{successCount} uploaded</span>}
                                {errorCount > 0 && <span className="text-xs font-medium text-red-700 bg-red-100 px-3 py-1 rounded-full">{errorCount} failed</span>}
                                {noMatchCount > 0 && <span className="text-xs font-medium text-orange-700 bg-orange-100 px-3 py-1 rounded-full">{noMatchCount} no match</span>}
                                {duplicateCount > 0 && <span className="text-xs font-medium text-red-700 bg-red-50 px-3 py-1 rounded-full border border-red-200">{duplicateCount} duplicate</span>}
                                {updateCount > 0 && <span className="text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1 rounded-full border border-blue-200">{updateCount} to update</span>}
                                {readyToUploadCount > 0 && <span className="text-xs font-medium text-green-700 bg-green-50 px-3 py-1 rounded-full border border-green-200">{readyToUploadCount} ready</span>}
                            </div>
                        )}
                    </div>

                    {/* Progress bar */}
                    {uploading && (
                        <div>
                            <p className="text-sm text-gray-500 mb-1">{uploadProgress}% complete</p>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                        </div>
                    )}

                    {/* Table */}
                    {rows.length > 0 && (
                        <Paper className="!shadow-sm !border !border-gray-200 overflow-hidden">
                            <Table size="small">
                                <TableHead>
                                    <TableRow className="bg-gray-100">
                                        <TableCell className="!font-semibold">Preview</TableCell>
                                        <TableCell className="!font-semibold">File Name</TableCell>
                                        <TableCell className="!font-semibold">Matched Product</TableCell>
                                        <TableCell className="!font-semibold">Status</TableCell>
                                        <TableCell className="!font-semibold" width={90}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => (
                                        <TableRow key={row.id} hover>
                                            {/* Preview */}
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="relative group">
                                                        <img
                                                            src={row.previewUrl}
                                                            alt={row.displayName}
                                                            className="w-14 h-14 object-contain rounded border border-gray-200 bg-white"
                                                            onError={(e) => {
                                                                console.error("New image failed to load:", row.previewUrl);
                                                            }}
                                                        />
                                                        <span className="absolute -top-1 -left-1 bg-blue-500 text-white text-[9px] font-bold px-1 rounded">NEW</span>
                                                    </div>

                                                    {row.matchedProduct?.imageUrl && (
                                                        <div className="relative group">
                                                            <ProductImage 
                                                                imageUrl={row.matchedProduct.imageUrl} 
                                                                name="current" 
                                                                size={56} 
                                                                className="opacity-50 hover:opacity-100 transition-opacity"
                                                            />
                                                            <span className="absolute -top-1 -left-1 bg-gray-500 text-white text-[9px] font-bold px-1 rounded">OLD</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>

                                            {/* File Name — editable */}
                                            <TableCell>
                                                {editingId === row.id ? (
                                                    <div className="flex items-center gap-1">
                                                        <Autocomplete
                                                            freeSolo
                                                            options={Array.from(new Set(products.map((p) => p.name)))}
                                                            inputValue={editValue}
                                                            onInputChange={(_, val) => setEditValue(val)}
                                                            onChange={(_, val) => { if (val) setEditValue(val); }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') commitEdit(row.id);
                                                                if (e.key === 'Escape') cancelEdit();
                                                            }}
                                                            sx={{ minWidth: 220 }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder="Type or search product name..."
                                                                    autoFocus
                                                                />
                                                            )}
                                                        />
                                                        <Tooltip title="Confirm">
                                                            <IconButton size="small" color="success" onClick={() => commitEdit(row.id)}>
                                                                <ConfirmIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel">
                                                            <IconButton size="small" onClick={cancelEdit}>
                                                                <CancelIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <span className="text-sm text-gray-700">{row.displayName}</span>
                                                        <br />
                                                        <span className="text-xs text-gray-400">
                                                            {(row.file.size / 1024).toFixed(1)} KB
                                                        </span>
                                                    </>
                                                )}
                                            </TableCell>

                                            {/* Matched Product */}
                                            <TableCell>
                                                {row.matchedProduct ? (
                                                    <span className="text-sm font-medium text-gray-800">
                                                        {row.matchedProduct.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-orange-500 italic">No matching product found</span>
                                                )}
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell>
                                                <StatusChip status={row.status} errorMessage={row.errorMessage} />
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell>
                                                <div className="flex items-center">
                                                    <Tooltip title="Rename to re-match product">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => startEdit(row)}
                                                                disabled={uploading || row.status === 'uploading' || row.status === 'success'}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Remove this image from upload list">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => removeRow(row.id)}
                                                                disabled={uploading || row.status === 'uploading'}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    {row.matchedProduct?.imageUrl && (
                                                        <Tooltip title="Delete OLD image from server">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => setDeleteConfirmParams({ productId: row.matchedProduct!.productId, productName: row.matchedProduct!.name })}
                                                                    disabled={uploading || row.status === 'uploading' || row.status === 'success'}
                                                                >
                                                                    <CloudOffIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Paper>
                    )}

                    {rows.length === 0 && !loadingProducts && (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                            <FolderOpenIcon style={{ fontSize: 64 }} />
                            <p className="mt-4 text-lg">No images selected yet</p>
                            <p className="text-sm">Click "Select Images" or "Select Folder" to get started</p>
                        </div>
                    )}
                </div>
            </div>
            <Dialog 
                open={!!deleteConfirmParams} 
                onClose={() => setDeleteConfirmParams(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="!font-bold">Delete Existing Image</DialogTitle>
                <DialogContent>
                    <p className="text-gray-600 mt-2">
                        Are you sure you want to permanently delete the existing image for <strong>"{deleteConfirmParams?.productName}"</strong> from the server?
                    </p>
                    <p className="text-sm text-red-500 mt-3 font-medium">
                        This action cannot be undone.
                    </p>
                </DialogContent>
                <DialogActions className="p-4 bg-gray-50 border-t">
                    <AppButton variant="secondary" onClick={() => setDeleteConfirmParams(null)}>
                        Cancel
                    </AppButton>
                    <button
                        onClick={executeRemoveServerImage}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        Yes, Delete Image
                    </button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={clearConfirmOpen} 
                onClose={() => setClearConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="!font-bold">Clear All Images</DialogTitle>
                <DialogContent>
                    <p className="text-gray-600 mt-2">
                        Are you sure you want to clear all images?
                    </p>
                    <p className="text-sm text-red-500 mt-3 font-medium">
                        This will remove all currently selected images from the upload list.
                    </p>
                </DialogContent>
                <DialogActions className="p-4 bg-gray-50 border-t">
                    <AppButton variant="secondary" onClick={() => setClearConfirmOpen(false)}>
                        Cancel
                    </AppButton>
                    <button
                        onClick={executeClear}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                        Yes, Clear All
                    </button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={uploadConfirmOpen} 
                onClose={() => setUploadConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="!font-bold">Confirm Upload</DialogTitle>
                <DialogContent>
                    <p className="text-gray-600 mt-2">
                        Are you sure you want to upload {pendingCount} image(s)?
                    </p>
                    {updateCount > 0 && (
                        <p className="text-sm text-blue-600 mt-2">
                            Note: {updateCount} product(s) already have images that will be replaced.
                        </p>
                    )}
                    {duplicateCount > 0 && (
                        <p className="text-sm text-red-600 mt-2">
                            Note: {duplicateCount} duplicate match(es) will be skipped.
                        </p>
                    )}
                </DialogContent>
                <DialogActions className="p-4 bg-gray-50 border-t">
                    <AppButton variant="secondary" onClick={() => setUploadConfirmOpen(false)}>
                        Cancel
                    </AppButton>
                    <button
                        onClick={() => {
                            setUploadConfirmOpen(false);
                            uploadAll();
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Yes, Upload
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
